/**
 * Keyboard-wedge scan assembly, platform neutral. A scanner in HID / keystroke mode types a barcode like a very
 * fast keyboard and ends it with Enter (or Tab). What separates that from a person typing is speed. This class
 * is the ONE implementation of "is this run of characters a scan"; the browser (Ops) and the Android app (TSD)
 * only translate their own events into `pushChar` / `terminate` / `tick`.
 *
 * Timing is a profile (`KeyboardConfig`), not a hard-coded constant: a slower scanner or a Bluetooth link with a
 * longer gap is a configuration change.
 */

export type KeyboardConfig = {
  /** Shortest run that can be a scan once a terminator arrives. */
  readonly minLength: number;
  /** Longest gap between two characters of one scan; a longer gap starts a new run (that was typing). */
  readonly interCharMs: number;
  /** Longest gap between the last character and Enter/Tab. Generous: its job is to drop a stale half-run. */
  readonly terminatorGapMs: number;
  /** A fast run with no terminator is committed after this much silence (suffix-less scanners, IME commit). null = off. */
  readonly idleCommitMs: number | null;
  /** The no-terminator commit needs at least this many characters (a person cannot type this fast anyway). */
  readonly idleCommitMinLength: number;
};

/**
 * Defaults, with the reasoning (ADR-091):
 *  - interCharMs 80: scanners are 1-20 ms apart; a Bluetooth link or a configured inter-character delay can reach
 *    ~50-60 ms. Fast typists are 100+ ms. The old 50/60 ms rejected slow-but-normal scanners; 80 keeps a margin
 *    against humans.
 *  - terminatorGapMs: browser 150 (Enter is sent by the scanner right after the last character); TSD 1000 because
 *    its capture field is not typed into by people and a late Enter is better delivered than lost.
 *  - idleCommit: 150 ms silence after >= 6 (TSD) / 8 (browser) fast characters.
 */
export const KEYBOARD_DEFAULTS_BROWSER: KeyboardConfig = {
  minLength: 6,
  interCharMs: 80,
  terminatorGapMs: 150,
  idleCommitMs: 150,
  idleCommitMinLength: 8,
};

export const KEYBOARD_DEFAULTS_DEVICE: KeyboardConfig = {
  minLength: 3,
  // 250, not 80 (field test 2026-10-27): on a slow handheld the JS thread pauses between characters of a long label
  // (a GS1 DataMatrix is 40+ characters), and with 80 ms real scans were refused as incomplete. The hidden capture
  // field is never typed into by people, so a larger gap cannot let typing pass as a scan; a split run is still flagged.
  interCharMs: 250,
  terminatorGapMs: 1000,
  idleCommitMs: 150,
  idleCommitMinLength: 6,
};

/** Longer than any barcode we accept (normalisation caps at 1024); a run beyond this is not a scan. */
export const MAX_RUN_LENGTH = 2048;

export type KeyboardVia = 'ENTER' | 'TAB' | 'NEWLINE' | 'SUBMIT_EDITING' | 'IDLE';

/**
 * Why a scan value cannot be trusted as the whole label. `RUN_SPLIT`: a gap longer than `interCharMs` fell INSIDE
 * this run's characters and the characters before it were thrown away, so the value may be only the tail of the
 * label (on a device whose JS thread stalls mid-scan the keys arrive late; a field-test scan of 4684615935436 came
 * out as 4615935436). The scan is still delivered, but flagged: no warehouse operation may be done from it.
 */
export type ScanSuspect = 'RUN_SPLIT';

export type AssembleOutcome =
  | { readonly kind: 'SCAN'; readonly value: string; readonly via: KeyboardVia; readonly maxGapMs: number; readonly suspect?: ScanSuspect }
  | { readonly kind: 'IGNORED'; readonly reason: 'NO_RUN' }
  | { readonly kind: 'REJECTED'; readonly reason: 'TOO_SHORT' | 'TERMINATOR_LATE'; readonly length: number; readonly detail: string };

export class KeyStreamAssembler {
  #cfg: KeyboardConfig;
  #chars: string[] = [];
  #lastAt: number | null = null;
  #maxGap = 0;
  #droppedRuns = 0;
  /** The run now being collected began right after a discarded partial run in the same burst. */
  #splitBefore = false;

  constructor(cfg: Partial<KeyboardConfig> = {}, base: KeyboardConfig = KEYBOARD_DEFAULTS_DEVICE) {
    this.#cfg = { ...base, ...cfg };
  }

  get config(): KeyboardConfig {
    return this.#cfg;
  }

  setConfig(cfg: Partial<KeyboardConfig>): void {
    this.#cfg = { ...this.#cfg, ...cfg };
  }

  /** Characters collected so far. The characters themselves are never exposed. */
  get length(): number {
    return this.#chars.length;
  }

  get lastCharAt(): number | null {
    return this.#lastAt;
  }

  /** Runs abandoned because the next character came too late (slow typing, a half scan). */
  get droppedRuns(): number {
    return this.#droppedRuns;
  }

  pushChar(c: string, t: number): void {
    if (this.#lastAt !== null && this.#chars.length > 0) {
      const gap = t - this.#lastAt;
      if (gap > this.#cfg.interCharMs || gap < 0) {
        this.#chars = [];
        this.#maxGap = 0;
        this.#droppedRuns += 1;
        this.#splitBefore = true;
      } else if (gap > this.#maxGap) {
        this.#maxGap = gap;
      }
    }
    this.#chars.push(c);
    this.#lastAt = t;
    // A stuck key stream (gaps always under the limit) must not grow without bound: drop the run.
    if (this.#chars.length > MAX_RUN_LENGTH) {
      this.#chars = [];
      this.#maxGap = 0;
      this.#droppedRuns += 1;
      this.#splitBefore = true;
    }
  }

  /** Enter / Tab / newline / submitEditing. Always leaves the assembler empty. */
  terminate(t: number, via: Exclude<KeyboardVia, 'IDLE'>): AssembleOutcome {
    const chars = this.#chars;
    const last = this.#lastAt;
    const maxGap = this.#maxGap;
    const split = this.#splitBefore;
    this.#chars = [];
    this.#maxGap = 0;
    this.#splitBefore = false;
    if (chars.length === 0 || last === null) return { kind: 'IGNORED', reason: 'NO_RUN' };
    if (chars.length < this.#cfg.minLength) {
      return { kind: 'REJECTED', reason: 'TOO_SHORT', length: chars.length, detail: `${chars.length} < ${this.#cfg.minLength}` };
    }
    const gap = t - last;
    if (gap > this.#cfg.terminatorGapMs || gap < 0) {
      return { kind: 'REJECTED', reason: 'TERMINATOR_LATE', length: chars.length, detail: `${Math.round(gap)} ms > ${this.#cfg.terminatorGapMs} ms` };
    }
    return { kind: 'SCAN', value: chars.join(''), via, maxGapMs: maxGap, ...(split ? { suspect: 'RUN_SPLIT' as const } : {}) };
  }

  /** Time-driven: commits a fast, long-enough run that never got a terminator. Returns null when nothing is due. */
  tick(t: number): AssembleOutcome | null {
    const idle = this.#cfg.idleCommitMs;
    if (idle === null || this.#chars.length === 0 || this.#lastAt === null) return null;
    if (t - this.#lastAt < idle) return null;
    if (this.#chars.length < Math.max(this.#cfg.idleCommitMinLength, this.#cfg.minLength)) return null;
    const value = this.#chars.join('');
    const maxGap = this.#maxGap;
    const split = this.#splitBefore;
    this.#chars = [];
    this.#maxGap = 0;
    this.#splitBefore = false;
    // No terminator was seen at all, so an IDLE commit can also be a label cut off in the middle: it is only
    // trusted when nothing was discarded before it (the caller still gets via 'IDLE' to decide further).
    return { kind: 'SCAN', value, via: 'IDLE', maxGapMs: maxGap, ...(split ? { suspect: 'RUN_SPLIT' as const } : {}) };
  }

  /** Drops a stale, too-short run (called from the same timer so a lone stray character cannot linger). */
  expire(t: number, olderThanMs: number): void {
    if (this.#lastAt !== null && this.#chars.length > 0 && t - this.#lastAt > olderThanMs) {
      this.#chars = [];
      this.#maxGap = 0;
      this.#splitBefore = false;
    }
  }

  reset(): void {
    this.#chars = [];
    this.#maxGap = 0;
    this.#lastAt = null;
    this.#splitBefore = false;
  }
}
